import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Gift, Copy, Share2, Users, Heart, Link as LinkIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Parrainage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/me/referral");
      setData(res.data);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) { navigate("/"); return; }
    if (user) load();
  }, [loading, user, navigate, load]);

  const copyLink = async () => {
    if (!data?.link) return;
    try { await navigator.clipboard.writeText(data.link); toast.success(t("referral.copied")); }
    catch { toast.error(t("memorial.error")); }
  };

  const share = async () => {
    if (!data?.link) return;
    const text = `${t("referral.shareText")} ${data.link}`;
    if (navigator.share) {
      try { await navigator.share({ title: "BougieDuCœur", text, url: data.link }); return; } catch { /* cancelled */ }
    }
    copyLink();
  };

  if (!user) return null;

  return (
    <div className="min-h-[100dvh]">
      <Header />
      <div className="mx-auto max-w-lg px-5 py-8 pb-28" data-testid="referral-page">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-amber-300/40 bg-amber-300/10 text-amber-300 shadow-[0_0_24px_-6px_rgba(251,191,36,0.6)]">
            <Gift size={26} />
          </div>
          <h1 className="text-2xl font-medium">{t("referral.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("referral.subtitle")}</p>
        </div>

        {fetching ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-sky-400" /></div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Users, label: t("referral.invited"), value: data.invited_count },
                { icon: Heart, label: t("referral.earned"), value: data.months_earned },
                { icon: Gift, label: t("referral.credit"), value: data.free_months_credit },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-card/40 p-4 text-center" data-testid={`referral-stat-${i}`}>
                  <s.icon size={18} className="mx-auto mb-1.5 text-amber-300" />
                  <div className="text-2xl font-semibold tabular-nums">{s.value}</div>
                  <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Lien */}
            <div className="mt-5 rounded-3xl border border-white/10 bg-card/40 p-5">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-sky-300">
                <LinkIcon size={16} /> {t("referral.yourLink")}
              </div>
              <div className="flex items-center gap-2 rounded-full border border-white/15 bg-black/40 px-4 py-2.5">
                <span className="min-w-0 flex-1 truncate text-sm text-amber-100" data-testid="referral-link">{data.link}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full border-white/15" onClick={copyLink} data-testid="referral-copy-btn">
                  <Copy size={15} className="mr-1.5" /> {t("referral.copy")}
                </Button>
                <Button className="flex-1 rounded-full" onClick={share} data-testid="referral-share-btn">
                  <Share2 size={15} className="mr-1.5" /> {t("referral.share")}
                </Button>
              </div>
            </div>

            {/* Comment ça marche */}
            <div className="mt-5 rounded-3xl border border-white/10 bg-card/40 p-5">
              <h2 className="mb-3 text-sm font-medium text-amber-200">{t("referral.how")}</h2>
              <ol className="space-y-3">
                {[t("referral.step1"), t("referral.step2"), t("referral.step3")].map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-amber-300/40 bg-amber-300/10 text-xs font-semibold text-amber-300">{i + 1}</span>
                    <span className="text-muted-foreground">{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            {data.free_months_credit > 0 && (
              <Button className="mt-5 w-full rounded-full border border-amber-300 bg-amber-300 font-semibold text-black hover:bg-amber-200" onClick={() => navigate("/mes-bougies")} data-testid="referral-use-credit-btn">
                <Gift size={16} className="mr-2" /> {t("referral.useCredit")}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
